import { getCarHubImageUrl } from '@/entities/car'

import { ANNUAL_DISTANCE_KM } from '../_constants'
import { calcAnnualFuelCost } from '../_utils'

import type {
  IDashboardSummaryResponse,
  IVehicleStatisticsDto,
  TApiFuelType,
} from './getModooGarageSummary'
import type { ICompareItem, IModoocarDetail, TFuelType } from '../_types'

/** API 유종 → 도메인 유종 표기 */
const FUEL_API_TO_DOMAIN: Record<string, TFuelType> = {
  GASOLINE_REGULAR: '휘발유',
  GASOLINE_PREMIUM: '휘발유',
  DIESEL: '디젤',
  LPG: 'LPG',
  EV: '전기',
}

/** 도메인 유종 → API 유종 (요청 파라미터용) */
export const FUEL_DOMAIN_TO_API: Record<TFuelType, TApiFuelType> = {
  휘발유: 'GASOLINE_REGULAR',
  디젤: 'DIESEL',
  LPG: 'LPG',
  하이브리드: 'GASOLINE_REGULAR',
  전기: 'EV',
}

/** 유종별 실측 평균 실연비 선택 */
const pickEfficiency = (
  stats: IVehicleStatisticsDto,
  apiFuel: string
): number => {
  switch (apiFuel) {
    case 'GASOLINE_PREMIUM':
      return stats.efficiencyPremium
    case 'DIESEL':
      return stats.efficiencyDiesel
    case 'LPG':
      return stats.efficiencyLpg
    case 'EV':
      return stats.efficiencyEv
    default:
      return stats.efficiencyGasoline
  }
}

const isEv = (apiFuel: string) => apiFuel === 'EV'
const fuelUnit = (apiFuel: string) => (isEv(apiFuel) ? 'km/kWh' : 'km/L')

/** 소수점 둘째자리 절사 */
const floor2 = (n: number) => Math.floor(n * 100) / 100

/** ISO 날짜('2026-07-01') → '2026년 7월' 표기 (형식 불일치 시 undefined) */
const formatReferenceMonth = (iso?: string): string | undefined => {
  const matched = iso?.match(/^(\d{4})-(\d{2})/)
  if (!matched) return undefined
  return `${matched[1]}년 ${Number(matched[2])}월`
}

/** ISO 날짜('2026-07-01') → '2026년 7월 1일' 풀 날짜 표기 (형식 불일치 시 undefined) */
const formatReferenceDate = (iso?: string): string | undefined => {
  const matched = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!matched) return undefined
  return `${matched[1]}년 ${Number(matched[2])}월 ${Number(matched[3])}일`
}

/** 연비 정보 없을 때 문구 (Confluence 세부기획 '연비 정보 없는 경우') */
const NO_DATA_AI_SUMMARY =
  '아직 마이클 실사용 리뷰가 없어요\n조금만 기다려 주세요!'

/** 전기차 충전 단가(원/kWh) — 기후에너지환경부 완속 100kW 미만 기준(세부기획 고정값) */
const ELECTRICITY_RATE_PER_KWH = 324.4

/** 추이 막대 6개 중 '최근'(실연비) 막대를 제외한 과거 월 수 — monthly 최신순으로 채운다 */
const TREND_HISTORY_MONTHS = 5

/** 공인연비 대비 비율(%) 정수 */
const ratioPct = (real: number, official: number) =>
  official > 0 ? Math.round((real / official) * 100) : 0

/**
 * AI 실사용 분석 문구 — Confluence 세부기획 템플릿(최신) (GEO 팩트 패시지).
 * 실연비(실전비)는 최소~최대 범위로 표기, 공인연비 대비 비율은 평균 실연비 기준 단일 %.
 * 케이스: 연료(전기 vs 휘발유/디젤/LPG) + 데이터 없음.
 */
const buildAiSummary = ({
  isElectric,
  ownerCount,
  brand,
  model,
  realEconomy,
  officialEconomy,
  realMin,
  realMax,
  ratePerUnit,
  rateYearMonth,
  annualFuelCostWon,
  monthlyFuelCostWon,
  perKmCost,
}: {
  isElectric: boolean
  ownerCount: number
  brand: string
  model: string
  realEconomy: number
  officialEconomy: number
  realMin?: number
  realMax?: number
  ratePerUnit: number
  rateYearMonth: string
  /** 연간 유류비(원) — API 제공 시 사용 */
  annualFuelCostWon?: number
  /** 월 유류비(원) — API 제공 시 사용 */
  monthlyFuelCostWon?: number
  /** 1km당 주행비용(원) — API 제공 시 사용 */
  perKmCost?: number
}): string => {
  // 실연비(실전비) 데이터 없으면 안내 문구
  if (realEconomy <= 0) return NO_DATA_AI_SUMMARY

  // 전기는 km/kWh·전기요금 기준 (문서 표기 km/L는 휘발유 템플릿 복붙 흔적으로 보여 단위는 정확하게 유지)
  const unit = isElectric ? 'km/kWh' : 'km/L'
  const realLabel = isElectric ? '실전비' : '실연비'
  const officialLabel = isElectric ? '공인전비' : '공인연비'
  const costLabel = isElectric ? '전기요금' : '유류비'
  const rateLabel = isElectric ? '평균 전기요금' : '평균 유가'
  const rateUnit = isElectric ? '원/kWh' : '원/L'

  // 실연비(실전비) 표기 — 최소~최대 범위 (범위 없거나 동일하면 평균 단일값)
  const hasRange =
    realMin != null &&
    realMax != null &&
    realMin > 0 &&
    realMax > 0 &&
    realMin !== realMax
  const realDisplay = hasRange ? `${realMin}~${realMax}` : String(realEconomy)
  // 공인연비 대비 비율 — 평균 실연비 기준 단일 %
  const ratio = ratioPct(realEconomy, officialEconomy)

  const cost = calcAnnualFuelCost({
    realFuelEconomy: realEconomy,
    fuelPricePerLiter: ratePerUnit,
    annualWon: annualFuelCostWon,
    monthlyWon: monthlyFuelCostWon,
    perKmCost,
  })

  // 공인연비(전비) 없으면 대비 문장 생략 (Figma 7547:9523 — 대비 지표 미노출)
  const officialSentence =
    officialEconomy > 0
      ? `${officialLabel} ${officialEconomy}${unit} 대비 ${ratio}% 수준으로 주행된다. `
      : ''

  return (
    `마이클 오너 ${ownerCount.toLocaleString()}명 실측 기준 ${brand} ${model}의 ${realLabel}는 ${realDisplay} ${unit}이다. ` +
    officialSentence +
    `연간 ${costLabel}는 주행거리 연 2만km, ${rateYearMonth} ${rateLabel} ${ratePerUnit.toLocaleString()}${rateUnit} 기준, ` +
    `약 ${cost.annualManWon.toLocaleString()}만원(월 약 ${cost.monthlyManWon.toLocaleString()}만원)으로 추산된다. ` +
    `1km당 주행비용은 ${cost.costPerKm}원이다.`
  )
}

/** 통계 항목의 차종명 — 통계의 vehicle은 null이라 id.model / modelUrlSafe에서 얻는다 */
const statsModelName = (stats: IVehicleStatisticsDto): string =>
  stats.id?.model ||
  (stats.modelUrlSafe ? decodeURIComponent(stats.modelUrlSafe) : '')

/** 차량 통계 → 차종 비교 카드 (비교는 조회 차종의 유종 기준으로 통일) */
const toCompareItem = ({
  stats,
  apiFuel,
  fuelPricePerLiter,
  modelName,
  brand,
  imageUrl,
  current,
  annualWon,
  monthlyWon,
}: {
  stats: IVehicleStatisticsDto
  apiFuel: string
  fuelPricePerLiter: number
  modelName: string
  /** 제조사 — 카드 클릭 시 해당 차종 상세 이동에 사용 */
  brand?: string
  imageUrl: string
  current: boolean
  /** 연간 유류비(원) — 현재 차종은 API값을 그대로 사용 */
  annualWon?: number
  /** 월 유류비(원) — 현재 차종은 API값을 그대로 사용 */
  monthlyWon?: number
}): ICompareItem => {
  const efficiency = floor2(pickEfficiency(stats, apiFuel))
  const cost = calcAnnualFuelCost({
    realFuelEconomy: efficiency,
    fuelPricePerLiter,
    annualWon,
    monthlyWon,
  })
  return {
    modelName,
    brand,
    imageUrl,
    efficiency: { value: String(efficiency), unit: fuelUnit(apiFuel) },
    fuelCost: { value: String(cost.annualManWon), unit: '만원/년' },
    satisfaction: stats.averageScore.toFixed(1),
    ownerCount: { value: stats.count.toLocaleString(), unit: '명' },
    current,
  }
}

/**
 * DashboardSummaryResponse → IModoocarDetail 매핑.
 * API가 주는 값(차종/실연비/통계/순위/차종 비교)은 응답에서, 그 외(6개월 추이·
 * 오너 후기 목록·만족 키워드·정비 후기·차 이미지·유가)는 fallback(mock)에서 가져온다.
 */
export const mapSummaryToDetail = (
  summary: IDashboardSummaryResponse,
  fallback: IModoocarDetail
): IModoocarDetail => {
  const { vehicle, macarStatistics, vehicleStatistics } = summary
  const apiFuel = vehicle.fuelType
  const analysis = summary.realUsageAnalysis
  // 6개월 실연비 추이 원본 (추이 렌더용)
  const apiMonthly = summary.fuelEfficiencyTrend?.monthly
  // 평균 실연비 — 백엔드 계산값(realUsageAnalysis.realFuelEfficiency)을 사용한다.
  // (게이지 중앙값 + 추이 '최근' 막대 공용). 값이 없으면 6개월 추이 산술평균 →
  // 유종별 통계 순으로 폴백한다.
  const realFuelEconomy =
    analysis?.realFuelEfficiency != null && analysis.realFuelEfficiency > 0
      ? floor2(analysis.realFuelEfficiency)
      : apiMonthly && apiMonthly.length
        ? floor2(
            apiMonthly.reduce((sum, m) => sum + m.efficiency, 0) /
              apiMonthly.length
          )
        : floor2(pickEfficiency(vehicleStatistics, apiFuel))
  // 공인연비(전비) — API 미제공이면 0(없음)으로 두고 화면에서 '정보 없음' 케이스 처리
  // (목업 폴백을 쓰면 남의 차 공인연비가 노출되므로 제거 — Figma 7547:9523)
  const officialFuelEconomy = Number.parseFloat(vehicle.fuelEconomy) || 0
  const ownerCount =
    analysis?.ownerCount || vehicleStatistics.count || macarStatistics.count
  const isElectric = isEv(apiFuel)

  // 유류비 — API(realUsageAnalysis) 연간/월 유류비를 우선 사용한다.
  // (계산식: 연간 유류비 = 연간주행거리 ÷ 실연비 × 유가)
  const apiAnnualFuelCost =
    analysis?.annualFuelCost && analysis.annualFuelCost > 0
      ? analysis.annualFuelCost
      : undefined
  const apiMonthlyFuelCost =
    analysis?.monthlyFuelCost && analysis.monthlyFuelCost > 0
      ? analysis.monthlyFuelCost
      : undefined
  const apiMileage =
    analysis?.annualMileage && analysis.annualMileage > 0
      ? analysis.annualMileage
      : ANNUAL_DISTANCE_KM
  // 서버 계산 1km당 비용·기준 유가 (신규 필드) — 있으면 그대로 사용(역산 불필요)
  const apiPerKmCost =
    analysis?.perKmCost && analysis.perKmCost > 0
      ? analysis.perKmCost
      : undefined
  const apiBasePrice =
    analysis?.basePrice && analysis.basePrice > 0
      ? Math.round(analysis.basePrice)
      : undefined

  // 기준 유가 — 서버 basePrice 우선, 없으면 연간 유류비에서 역산(구 데이터),
  // 그것도 없으면 분기 고정값(GEO 인용 안정성) fallback 사용.
  const { fuelPriceYearMonth } = fallback.fuelCost
  const fuelPricePerLiter =
    apiBasePrice ??
    (apiAnnualFuelCost != null && realFuelEconomy > 0 && !isElectric
      ? Math.round((apiAnnualFuelCost * realFuelEconomy) / apiMileage)
      : fallback.fuelCost.fuelPricePerLiter)

  // AI 실사용 분석 단가 — 전기는 서버 basePrice(원/kWh) 우선, 없으면 고정 충전단가.
  // 액체연료는 위에서 정한 기준 유가.
  const ratePerUnit = isElectric
    ? (apiBasePrice ?? ELECTRICITY_RATE_PER_KWH)
    : fuelPricePerLiter
  // 실연비(실전비) 범위 — API 제공 min/max(평균 실연비를 포함하는 실측 범위). 소수점 둘째자리 절사.
  const realMinRaw =
    analysis?.officialFuelEfficiencyMin ?? vehicle.minFuelEfficiency
  const realMaxRaw =
    analysis?.officialFuelEfficiencyMax ?? vehicle.maxFuelEfficiency
  const realMin = realMinRaw != null ? floor2(realMinRaw) : undefined
  const realMax = realMaxRaw != null ? floor2(realMaxRaw) : undefined

  // 6개월 추이 — 가장 최근('최근') 막대는 평균 실연비, 나머지는 monthly 최신 5개월.
  // API 월별 데이터가 없으면 빈 추이로 두고 화면에서 추이 영역을 숨긴다.
  // (이전에는 목업 추이를 실연비 평균에 맞춰 늘려 실데이터처럼 노출했다)
  let trend: typeof fallback.realFuel.trend = []
  let trendMonthCount = 0
  if (apiMonthly && apiMonthly.length) {
    // 최신순 정렬 → 최근 5개월을 과거 막대로(오래된→최신), 마지막 '최근' 막대는 실연비로 채운다.
    const sortedDesc = [...apiMonthly].sort(
      (a, b) => b.year * 12 + b.month - (a.year * 12 + a.month)
    )
    const history = sortedDesc
      .slice(0, TREND_HISTORY_MONTHS)
      .reverse()
      .map(m => ({ month: `${m.month}월`, value: floor2(m.efficiency) }))
    trendMonthCount = history.length
    // '최근' 막대 월 키 — 최신 월 다음 달(렌더는 '최근'으로 표기, month는 key/aria용)
    const latestMonth = sortedDesc[0].month
    const recentMonth = latestMonth === 12 ? 1 : latestMonth + 1
    trend =
      realFuelEconomy > 0
        ? [...history, { month: `${recentMonth}월`, value: realFuelEconomy }]
        : history
  }

  // 페이지의 모든 '{YYYY}년 {N}월' 표기(데이터 기준·유가 기준·하단 고지·AI문구)를
  // 추이(fuelEfficiencyTrend.monthly)의 최신 월로 통일한다. 추이 없으면 fallback 유지.
  const latestMonthly =
    apiMonthly && apiMonthly.length
      ? apiMonthly.reduce((a, b) =>
          b.year * 12 + b.month > a.year * 12 + a.month ? b : a
        )
      : null
  // 데이터 기준 시점(월) — 서버 referenceDate 우선, 없으면 추이 최신 월, 그것도 없으면 fallback
  const dataYearMonth =
    formatReferenceMonth(summary.referenceDate) ??
    (latestMonthly
      ? `${latestMonthly.year}년 ${latestMonthly.month}월`
      : fuelPriceYearMonth)
  // 데이터 기준 일자(풀 날짜) — referenceDate 있으면 'YYYY년 M월 D일', 없으면 월 표기로 폴백
  const dataBasisDate =
    formatReferenceDate(summary.referenceDate) ?? dataYearMonth

  // 선택 유종 — API availableFuelTypes 매핑(중복 제거), 없으면 fallback
  const mappedFuels = (summary.availableFuelTypes ?? [])
    .map(f => FUEL_API_TO_DOMAIN[f])
    .filter((v): v is TFuelType => Boolean(v))
  const fuelOptions = mappedFuels.length
    ? [...new Set(mappedFuels)]
    : fallback.fuelOptions
  // 유류비 상위% — API 제공 시 실데이터
  const fuelCostPercentile =
    summary.fuelCostTopPercent != null
      ? summary.fuelCostTopPercent
      : fallback.fuelCostPercentile

  return {
    ...fallback,
    brand: vehicle.brand,
    brandIcon: vehicle.brandIcon || fallback.brandIcon,
    // 대표 이미지는 세부모델(vehicle.model)로 생성 — 상위모델 대체 금지, 실패 시 폴백
    carImageUrl: getCarHubImageUrl(vehicle.brand, vehicle.model),
    upperModelName: vehicle.car,
    lowerModelName: vehicle.model,
    // 세그먼트 없는 차종은 mock('대형 SUV')으로 폴백하지 않고 빈 값 — 랭킹 칩·차종 비교를 숨긴다
    segment: vehicle.segment || '',
    fuelType: FUEL_API_TO_DOMAIN[apiFuel] ?? fallback.fuelType,
    fuelOptions,
    // 오너 리뷰 수 — 세부모델 글 있는 리뷰(목록 API와 같은 조건). null(장애)이면 0 → 미노출
    ownerReviewCount: macarStatistics.modelOwnerReviewCount ?? 0,
    // 차종(상위모델) 오너 리뷰 수 — 오너 후기 더보기 CTA 문구용
    carOwnerReviewCount: macarStatistics.carOwnerReviewCount ?? 0,
    // 순위 — 모터사이클 등 순위 미산정 차종은 null로 내려와 0(미노출) 처리
    satisfactionRank: summary.rankInScore ?? 0,
    efficiencyRank: summary.rankInEfficiency ?? 0,
    fuelCostPercentile,
    // 하단 CTA '오너 N명이 마이클로 관리중' — AI 분석/실연비와 동일한 실측 오너수 사용
    managingUserCount: ownerCount,
    aiSummary: buildAiSummary({
      isElectric,
      ownerCount,
      brand: vehicle.brand,
      model: vehicle.model,
      realEconomy: realFuelEconomy,
      officialEconomy: officialFuelEconomy,
      realMin,
      realMax,
      ratePerUnit,
      rateYearMonth: dataYearMonth,
      annualFuelCostWon: apiAnnualFuelCost,
      monthlyFuelCostWon: apiMonthlyFuelCost,
      perKmCost: apiPerKmCost,
    }),
    // 하단 고지 데이터 기준 시점 — 추이 최신 월(월) + 풀 날짜(일까지)
    dataBasisYearMonth: dataYearMonth,
    dataBasisDate,
    fuelCost: {
      ...fallback.fuelCost,
      fuelPricePerLiter,
      fuelPriceYearMonth: dataYearMonth,
      // API 제공 시 연간/월 유류비를 그대로 노출 (역산 유가로는 반올림 오차가 생기므로 원본 사용)
      annualFuelCostWon: apiAnnualFuelCost,
      monthlyFuelCostWon: apiMonthlyFuelCost,
      costPerKm: apiPerKmCost,
    },
    realFuel: {
      ...fallback.realFuel,
      ownerCount,
      realFuelEconomy,
      officialFuelEconomy,
      trend,
      trendMonthCount,
      // 실연비 데이터 기준 시점 — 추이 최신 월
      basisYearMonth: dataYearMonth,
    },
    ownerReview: {
      ...fallback.ownerReview,
      satisfactionRank: summary.rankInScore ?? 0,
      // API가 세그먼트를 안 주는 차종은 mock('대형 SUV')으로 폴백하지 않고 빈 값으로 둔다.
      // (빈 값이면 요약 카드에서 세그먼트 만족도 순위를 미노출 — ReviewSummaryCard 처리)
      segment: vehicle.segment || '',
      averageScore: macarStatistics.averageScore,
      // 만족 키워드·후기 목록은 API 미제공 → fallback 유지 (후기 별도 전달 예정)
    },
    // 조회 차종(현재) 카드 + 세그먼트 내 다른 차종 카드. 이미지는 API 미제공 →
    // mock 플레이스홀더(또는 차종 이미지)로 대체.
    compare: [
      toCompareItem({
        stats: vehicleStatistics,
        apiFuel,
        fuelPricePerLiter,
        modelName: vehicle.car,
        brand: vehicle.brand,
        // 현재 차종 카드 이미지는 CarCompareSection이 세부모델로 재생성(currentCarImageUrls)하지만
        // 값 자체도 세부모델(vehicle.model)로 맞춰 상위모델 URL이 남지 않게 한다
        imageUrl: getCarHubImageUrl(vehicle.brand, vehicle.model),
        current: true,
        annualWon: apiAnnualFuelCost,
        monthlyWon: apiMonthlyFuelCost,
      }),
      ...(summary.vehicleStatisticsInSegment ?? []).map(s =>
        toCompareItem({
          stats: s,
          apiFuel,
          fuelPricePerLiter,
          modelName: statsModelName(s),
          brand: s.id?.brand ?? vehicle.brand,
          // 세그먼트는 차종명이 없어 모델명으로 best-effort (미존재 시 카드가 모델명 텍스트 폴백)
          imageUrl: getCarHubImageUrl(
            s.id?.brand ?? vehicle.brand,
            statsModelName(s)
          ),
          current: false,
        })
      ),
    ],
  }
}
